<template>
  <div class="landing">

    <!-- HERO -->
    <section class="hero">
      <div class="hero-inner">
        <div class="badge">AI Receptionist for Dental Clinics</div>

        <h1>Never miss a patient call again</h1>

        <p class="subtitle">
          Your AI answers calls, books appointments, and sends confirmations instantly.
        </p>

        <div class="actions">
          <button class="btn-primary">Book Appointment</button>
          <a href="tel:+14385339360" class="btn-secondary">
            Call (438) 533-9360
          </a>
        </div>
      </div>
    </section>

    <!-- FEATURES -->
    <section class="features">
      <div class="feature">
        <h3>24/7 Availability</h3>
        <p>Never miss a call again.</p>
      </div>

      <div class="feature">
        <h3>Instant Booking</h3>
        <p>Appointments booked automatically.</p>
      </div>

      <div class="feature">
        <h3>SMS Confirmation</h3>
        <p>Patients receive confirmation instantly.</p>
      </div>
    </section>

    <!-- CHAT BUTTON -->
    <div class="chat-toggle" @click="toggleChat">
      💬
    </div>

    <!-- CHAT -->
    <div class="chat-window" v-if="isChatOpen">

      <div class="chat-header">
        <strong>Dental Assistant</strong>
        <button @click="toggleChat">✕</button>
      </div>

      <div class="chat-history" ref="chatContainer">

        <div
          v-for="(message, index) in chatHistory"
          :key="index"
          :class="['message-row', message.role]"
        >
          <div class="bubble" :class="message.role">
            {{ message.content }}
          </div>
        </div>

        <div v-if="isTyping" class="typing">
          <span></span><span></span><span></span>
        </div>

      </div>

      <div class="chat-input">
        <input
          v-model="userMessage"
          @keyup.enter="sendMessage"
          placeholder="Ask anything..."
        />
        <button @click="sendMessage">→</button>
      </div>

    </div>

  </div>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      apiUri: process.env.VUE_APP_API_URL || "http://localhost:3010",
      isChatOpen: false,
      userMessage: "",
      isTyping: false,
      sessionId: null,
      chatHistory: [
        {
          role: "assistant",
          content: "Hi! How can I help you today?"
        }
      ]
    };
  },

  watch: {
    chatHistory() {
      this.scrollToBottom();
    }
  },

  mounted() {
    this.scrollToBottom();
  },

  methods: {
    toggleChat() {
      this.isChatOpen = !this.isChatOpen;
    },

    async sendMessage() {
      const messageText = this.userMessage.trim();
      if (!messageText) return;

      this.chatHistory.push({
        role: "user",
        content: messageText
      });

      this.userMessage = "";
      this.isTyping = true;

      try {
        let response;

        if (!this.sessionId) {
          response = await axios.post(
            this.apiUri + "/api/chat/public/auto",
            {
              agent_id: 3,
              message: messageText
            }
          );

          this.sessionId = response.data.session_id;

        } else {
          response = await axios.get(
            this.apiUri + `/api/chat/public/${this.sessionId}`,
            {
              params: { message: messageText }
            }
          );
        }

        const reply = response.data.response;

        this.chatHistory.push({
          role: "assistant",
          content: reply
        });

      } catch (err) {
        console.error(err);

        this.chatHistory.push({
          role: "assistant",
          content: "Something went wrong. Please try again."
        });
      } finally {
        this.isTyping = false;
      }
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.chatContainer;
        if (el) {
          el.scrollTop = el.scrollHeight;
        }
      });
    }
  }
};
</script>

<style>
body {
  margin: 0;
  font-family: Inter, sans-serif;
  background: #f9fafb;
}

/* HERO */
.hero {
  padding: 140px 20px 100px;
  text-align: center;
  background: linear-gradient(180deg, #ffffff, #f3f4f6);
}

.hero h1 {
  font-size: 3.8rem;
  font-weight: 800;
}

.subtitle {
  margin-top: 20px;
  color: #6b7280;
}

.badge {
  display: inline-block;
  padding: 8px 16px;
  background: #eef2ff;
  color: #4338ca;
  border-radius: 999px;
  margin-bottom: 20px;
}

/* BUTTONS */
.btn-primary {
  background: #2563eb;
  color: white;
  padding: 14px 30px;
  border-radius: 999px;
}

.btn-secondary {
  margin-left: 10px;
  color: #2563eb;
}

/* FEATURES */
.features {
  display: flex;
  justify-content: center;
  gap: 40px;
  padding: 80px;
}

.feature {
  background: white;
  padding: 30px;
  border-radius: 16px;
  width: 240px;
  text-align: center;
  box-shadow: 0 10px 40px rgba(0,0,0,0.05);
}

/* CHAT BUTTON */
.chat-toggle {
  position: fixed;
  bottom: 25px;
  right: 25px;
  width: 65px;
  height: 65px;
  background: #2563eb;
  color: white;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 26px;
  cursor: pointer;
  box-shadow: 0 20px 60px rgba(0,0,0,0.3);
}

/* CHAT */
.chat-window {
  position: fixed;
  bottom: 110px;
  right: 25px;
  width: 380px;
  height: 540px;
  background: white;
  border-radius: 24px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 40px 120px rgba(0,0,0,0.25);
  overflow: hidden;
}

.chat-header {
  padding: 16px;
  background: #f9fafb;
  display: flex;
  justify-content: space-between;
}

.chat-history {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}

.message-row {
  display: flex;
  margin-bottom: 12px;
}

.message-row.user {
  justify-content: flex-end;
}

.bubble {
  padding: 10px 14px;
  border-radius: 18px;
  max-width: 70%;
}

.bubble.user {
  background: #2563eb;
  color: white;
}

.bubble.assistant {
  background: #f3f4f6;
}

/* INPUT */
.chat-input {
  display: flex;
  border-top: 1px solid #eee;
}

.chat-input input {
  flex: 1;
  border: none;
  padding: 14px;
  border-bottom-left-radius: 24px;
}

.chat-input button {
  background: #2563eb;
  color: white;
  border: none;
  padding: 0 20px;
}

/* TYPING */
.typing span {
  width: 6px;
  height: 6px;
  background: #2563eb;
  margin: 3px;
  border-radius: 50%;
  display: inline-block;
  animation: bounce 1s infinite;
}

@keyframes bounce {
  0%,100% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
}
</style>